import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Menu as MenuIcon, Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, Save, RotateCcw,
  Eye, Smartphone, Monitor, ExternalLink, FileDown, Loader2, GripVertical,
  BarChart3, ShieldCheck, MousePointerClick, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { api } from '../../lib/api';

/* Admin → Menu Manager
   Edits the grouped public navigation (desktop dropdowns + mobile hamburger
   accordions). The whole structure is saved as one configuration; Restore
   Defaults brings back the approved menu. */

const newId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const EMPTY_ITEM = () => ({
  id: newId(), label: '', url: '', mobile_label: '', description: '', icon: '',
  external: false, new_tab: false, download: false, heading: false, show: true,
});
const EMPTY_GROUP = () => ({
  id: newId(), label: '', desktop_label: '', mobile_label: '', icon: '', url: '',
  show: true, show_desktop: true, show_mobile: true, items: [],
});

const move = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [x] = copy.splice(from, 1);
  copy.splice(to, 0, x);
  return copy;
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

/* ---------------- Item editor row ---------------- */
const ItemRow = ({ item, gi, ii, groups, onChange, onMove, onMoveToGroup, onDelete }) => {
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ ...item, [k]: v });
  return (
    <div className={`border rounded-lg ${item.show ? 'border-slate-200 bg-white' : 'border-dashed border-slate-300 bg-slate-50 opacity-70'}`} data-testid={`menu-item-row-${item.id}`}>
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
        <button onClick={() => setOpen((v) => !v)} className="flex-1 flex items-center gap-2 text-left min-w-0" data-testid={`menu-item-toggle-${item.id}`}>
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
          <span className={`text-sm font-bold truncate ${item.heading ? 'uppercase tracking-wider text-slate-500 text-[11px]' : 'text-[#1a2744]'}`}>{item.label || '(unnamed item)'}</span>
          {item.heading ? <Badge variant="outline" className="text-[10px] shrink-0">Heading</Badge> : <span className="text-[11px] text-slate-400 truncate hidden sm:inline">{item.url}</span>}
          {item.download && <FileDown className="h-3 w-3 text-[#8a6d2c] shrink-0" />}
          {(item.external || item.new_tab) && <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />}
          {!item.show && <Badge variant="outline" className="text-[10px] shrink-0">Hidden</Badge>}
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <Switch checked={!!item.show} onCheckedChange={(v) => set('show', v)} className="scale-75" data-testid={`menu-item-show-${item.id}`} />
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onMove(-1)} disabled={ii === 0} aria-label="Move up" data-testid={`menu-item-up-${item.id}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onMove(1)} aria-label="Move down" data-testid={`menu-item-down-${item.id}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={onDelete} aria-label="Delete item" data-testid={`menu-item-delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2.5 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Field label="Label"><Input value={item.label} onChange={(e) => set('label', e.target.value)} className="h-10" data-testid={`menu-item-label-${item.id}`} /></Field>
            {!item.heading && <Field label="Link (page, page#section, /api/ download, or full https:// URL)"><Input value={item.url} onChange={(e) => set('url', e.target.value)} className="h-10" placeholder="/about or /elections/key-dates or https://…" data-testid={`menu-item-url-${item.id}`} /></Field>}
            <Field label="Mobile label (optional, shorter)"><Input value={item.mobile_label || ''} onChange={(e) => set('mobile_label', e.target.value)} className="h-10" /></Field>
            <Field label="Short description (optional)"><Input value={item.description || ''} onChange={(e) => set('description', e.target.value)} className="h-10" /></Field>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Section heading (not clickable) <Switch checked={!!item.heading} onCheckedChange={(v) => set('heading', v)} className="scale-90" data-testid={`menu-item-heading-${item.id}`} /></label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">External link <Switch checked={!!item.external} onCheckedChange={(v) => set('external', v)} className="scale-90" /></label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Open in new tab <Switch checked={!!item.new_tab} onCheckedChange={(v) => set('new_tab', v)} className="scale-90" data-testid={`menu-item-newtab-${item.id}`} /></label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Download link <Switch checked={!!item.download} onCheckedChange={(v) => set('download', v)} className="scale-90" /></label>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
              Move to group
              <select
                value={gi}
                onChange={(e) => onMoveToGroup(Number(e.target.value))}
                className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs font-semibold text-[#1a2744]"
                data-testid={`menu-item-move-group-${item.id}`}
              >
                {groups.map((g, i) => <option key={g.id} value={i}>{g.label || `(group ${i + 1})`}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Group card ---------------- */
const GroupCard = ({ group, gi, groups, onChange, onMove, onDelete, onItemMoveToGroup }) => {
  const set = (k, v) => onChange({ ...group, [k]: v });
  const setItems = (items) => onChange({ ...group, items });
  return (
    <Card className={`border-slate-200 ${group.show ? '' : 'opacity-70'}`} data-testid={`menu-group-card-${group.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Input value={group.label} onChange={(e) => set('label', e.target.value)} placeholder="Group label (e.g. Start Here)" className="h-10 font-bold flex-1 min-w-[180px]" data-testid={`menu-group-label-${group.id}`} />
          <div className="flex items-center gap-0.5">
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onMove(-1)} disabled={gi === 0} aria-label="Move group up" data-testid={`menu-group-up-${group.id}`}><ChevronUp className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onMove(1)} aria-label="Move group down" data-testid={`menu-group-down-${group.id}`}><ChevronDown className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={onDelete} aria-label="Delete group" data-testid={`menu-group-delete-${group.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="mb-3">
          <Field label="Direct link for the top-level entry (optional). With a link and no items, it is a plain button (Home, Request a Sign, Contact, Donate). With items too, the dropdown gets an Open button.">
            <Input value={group.url || ''} onChange={(e) => set('url', e.target.value)} className="h-10" placeholder="/about" data-testid={`menu-group-url-${group.id}`} />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
          <Field label="Shorter desktop label (optional, keeps the top bar clean)"><Input value={group.desktop_label || ''} onChange={(e) => set('desktop_label', e.target.value)} className="h-10" placeholder={group.label} /></Field>
          <Field label="Mobile label (optional)"><Input value={group.mobile_label || ''} onChange={(e) => set('mobile_label', e.target.value)} className="h-10" placeholder={group.label} /></Field>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3">
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Visible <Switch checked={!!group.show} onCheckedChange={(v) => set('show', v)} className="scale-90" data-testid={`menu-group-show-${group.id}`} /></label>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600"><Monitor className="h-3.5 w-3.5" /> Desktop menu <Switch checked={group.show_desktop !== false} onCheckedChange={(v) => set('show_desktop', v)} className="scale-90" data-testid={`menu-group-desktop-${group.id}`} /></label>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600"><Smartphone className="h-3.5 w-3.5" /> Hamburger menu <Switch checked={group.show_mobile !== false} onCheckedChange={(v) => set('show_mobile', v)} className="scale-90" data-testid={`menu-group-mobile-${group.id}`} /></label>
        </div>
        <div className="space-y-1.5" data-testid={`menu-group-items-${group.id}`}>
          {group.items.map((item, ii) => (
            <ItemRow
              key={item.id} item={item} gi={gi} ii={ii} groups={groups}
              onChange={(updated) => setItems(group.items.map((x, i) => (i === ii ? updated : x)))}
              onMove={(dir) => setItems(move(group.items, ii, ii + dir))}
              onMoveToGroup={(targetGi) => onItemMoveToGroup(gi, ii, targetGi)}
              onDelete={() => {
                if (window.confirm(`Delete menu item "${item.label || 'unnamed'}"?\n\nTip: if you might want it back later, turn its visibility OFF instead of deleting.`)) {
                  setItems(group.items.filter((_, i) => i !== ii));
                }
              }}
            />
          ))}
          <Button variant="outline" size="sm" className="h-9 text-xs font-bold border-dashed border-slate-300 text-slate-600 w-full" onClick={() => setItems([...group.items, EMPTY_ITEM()])} data-testid={`menu-group-add-item-${group.id}`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add menu item
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Read-only previews ---------------- */
const DesktopPreview = ({ groups }) => (
  <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="menu-preview-desktop">
    <div className="bg-[#1a2744] text-white px-4 py-2.5 flex items-center gap-2 flex-wrap">
      <span className="font-serif font-black text-sm mr-2">Vote Steve Lesperance</span>
      {groups.filter((g) => g.show && g.show_desktop !== false).map((g) => (
        <span key={g.id} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold bg-white/10">{g.desktop_label || g.label} {g.items.some((i) => i.show && !i.heading) && <ChevronDown className="h-3 w-3" />}</span>
      ))}
      <span className="ml-auto px-2 py-1 rounded text-[11px] font-bold bg-[#C9A84C] text-[#1a2744]">Report an Issue</span>
    </div>
    <div className="bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
      {groups.filter((g) => g.show && g.show_desktop !== false).map((g) => (
        <div key={g.id} className="bg-white rounded-lg border border-slate-200 p-2">
          <p className="text-[10px] font-black uppercase tracking-wide text-slate-400 mb-1">{g.desktop_label || g.label}</p>
          {g.items.filter((i) => i.show).map((i) => (
            <p key={i.id} className={`py-0.5 flex items-center gap-1 truncate ${i.heading ? 'text-[9px] font-black uppercase tracking-wider text-slate-400 mt-1' : 'text-[11px] font-semibold text-[#1a2744]'}`}>
              {i.download && <FileDown className="h-2.5 w-2.5 text-[#8a6d2c] shrink-0" />}{i.label}
              {(i.external || i.new_tab) && !i.download && <ExternalLink className="h-2.5 w-2.5 text-slate-400 shrink-0" />}
            </p>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const MobilePreview = ({ groups }) => (
  <div className="max-w-[300px] rounded-xl overflow-hidden border border-slate-200 bg-[#1a2744] text-white" data-testid="menu-preview-mobile">
    <div className="px-4 py-3 border-b border-white/10">
      <p className="font-serif font-black text-sm">Vote Steve Lesperance</p>
      <p className="text-[9px] font-bold tracking-[0.2em] text-[#C9A84C] uppercase">Ward 8 Councillor</p>
    </div>
    {groups.filter((g) => g.show && g.show_mobile !== false).map((g) => (
      <div key={g.id} className="border-b border-white/10">
        <p className="px-4 py-2.5 text-[13px] font-bold flex items-center justify-between">{g.mobile_label || g.label} <ChevronDown className="h-3 w-3 text-[#C9A84C]" /></p>
        <div className="px-4 pb-2">
          {g.items.filter((i) => i.show).slice(0, 3).map((i) => (
            <p key={i.id} className="text-[11px] text-white/80 py-1 flex items-center gap-1 truncate">
              {i.download && <FileDown className="h-2.5 w-2.5 text-[#C9A84C] shrink-0" />}{(i.mobile_label || i.label)}
              {(i.external || i.new_tab) && !i.download && <ExternalLink className="h-2.5 w-2.5 text-white/40 shrink-0" />}
            </p>
          ))}
          {g.items.filter((i) => i.show).length > 3 && <p className="text-[10px] text-white/40 pb-1">…and {g.items.filter((i) => i.show).length - 3} more</p>}
        </div>
      </div>
    ))}
  </div>
);

/* ---------------- Analytics tab (privacy-safe aggregate counters) ---------------- */
const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/nav-menu/analytics')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load menu analytics'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async () => {
    if (!window.confirm('Reset all menu click counters to zero?\n\nThis cannot be undone.')) return;
    try {
      await api.post('/admin/nav-menu/analytics/reset');
      toast.success('Menu click counters reset.');
      load();
    } catch { toast.error('Could not reset the counters'); }
  };

  if (loading && !data) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const rows = data?.rows || [];
  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div className="space-y-4" data-testid="menu-analytics-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">Total menu clicks</p>
          <p className="text-3xl font-black text-[#1a2744] mt-1" data-testid="menu-analytics-total">{data?.total ?? 0}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">Most used link</p>
          <p className="text-sm font-bold text-[#1a2744] mt-2 leading-snug" data-testid="menu-analytics-top-link">{data?.top_link || 'No clicks recorded yet'}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">Last reset</p>
          <p className="text-sm font-bold text-[#1a2744] mt-2">{data?.last_reset ? data.last_reset.slice(0, 10) : 'Never'}</p>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="font-bold text-[#1a2744] flex items-center gap-1.5 text-sm"><MousePointerClick className="h-4 w-4" /> Which menu links residents use most</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={load} className="h-9 text-xs font-bold border-slate-300" data-testid="menu-analytics-refresh-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
              <Button variant="outline" size="sm" onClick={reset} className="h-9 text-xs font-bold border-slate-300 text-red-600" data-testid="menu-analytics-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset counters</Button>
            </div>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="menu-analytics-empty">
              No menu clicks recorded yet. Counts appear here as visitors use the site menus.
            </p>
          ) : (
            <div className="space-y-2" data-testid="menu-analytics-rows">
              {rows.map((r, i) => (
                <div key={r.key} className="flex items-center gap-3" data-testid={`menu-analytics-row-${i}`}>
                  <span className="w-6 text-xs font-black text-slate-400 text-right shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-bold text-[#1a2744] truncate">{r.label}</p>
                      <p className="text-sm font-black text-[#1a2744] shrink-0" data-testid={`menu-analytics-count-${i}`}>{r.count}</p>
                    </div>
                    <p className="text-[11px] text-slate-400 truncate">{r.group_label}{r.url ? ` · ${r.url}` : ''}{r.last_click_day ? ` · last used ${r.last_click_day}` : ''}</p>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
                      <div className="h-full rounded-full bg-[#C9A84C]" style={{ width: `${Math.max(4, Math.round((r.count / max) * 100))}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 bg-slate-50"><CardContent className="p-4 text-xs text-slate-600">
        <p className="font-bold text-[#1a2744] flex items-center gap-1.5 mb-1"><ShieldCheck className="h-3.5 w-3.5" /> Privacy</p>
        <p data-testid="menu-analytics-privacy-note">{data?.privacy_note || 'Anonymous aggregate totals only. No IP addresses, cookies, personal data, or individual visitor tracking are stored.'}</p>
      </CardContent></Card>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function AdminMenu() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState('');

  const load = useCallback(() => {
    api.get('/admin/nav-menu').then((r) => { setCfg(r.data.config); setDirty(false); })
      .catch(() => toast.error('Failed to load the menu'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setGroups = (groups) => { setCfg({ groups }); setDirty(true); };

  const moveItemToGroup = (fromGi, ii, toGi) => {
    if (fromGi === toGi) return;
    const groups = cfg.groups.map((g) => ({ ...g, items: [...g.items] }));
    const [item] = groups[fromGi].items.splice(ii, 1);
    groups[toGi].items.push(item);
    setGroups(groups);
    toast.success(`Moved "${item.label}" to "${groups[toGi].label}"`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/nav-menu', cfg);
      setCfg(r.data);
      setDirty(false);
      toast.success('Menu saved — the public site header and hamburger menu are updated.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the menu'); }
    finally { setSaving(false); }
  };

  const restoreDefaults = async () => {
    if (!window.confirm('Restore the default menu structure?\n\nThis replaces your current menu with the approved default groups and links. Your current layout will be lost.')) return;
    try {
      const r = await api.post('/admin/nav-menu/reset');
      setCfg(r.data);
      setDirty(false);
      toast.success('Default menu restored.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not restore defaults'); }
  };

  if (!cfg) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-menu-manager">
      <div>
        <h1 className="font-serif text-2xl font-black text-[#1a2744] flex items-center gap-2"><MenuIcon className="h-6 w-6" /> Menu Manager</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Edit the public site menus: the top navigation dropdowns and the right hamburger menu.
          Links can point to a page, a section on a page (like <code className="text-xs bg-slate-100 px-1 rounded">/support-pita#share</code>), a download, or an outside website.
          Hide items instead of deleting them if you might want them back.
        </p>
      </div>

      <Tabs defaultValue="editor">
        <TabsList className="h-11">
          <TabsTrigger value="editor" className="text-sm font-bold px-4" data-testid="menu-tab-editor"><MenuIcon className="h-4 w-4 mr-1.5" /> Menu editor</TabsTrigger>
          <TabsTrigger value="analytics" className="text-sm font-bold px-4" data-testid="menu-tab-analytics"><BarChart3 className="h-4 w-4 mr-1.5" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setPreview(preview === 'desktop' ? '' : 'desktop')} className="h-10 text-xs font-bold border-slate-300" data-testid="menu-preview-desktop-btn"><Monitor className="h-3.5 w-3.5 mr-1.5" /> Desktop preview</Button>
            <Button variant="outline" onClick={() => setPreview(preview === 'mobile' ? '' : 'mobile')} className="h-10 text-xs font-bold border-slate-300" data-testid="menu-preview-mobile-btn"><Smartphone className="h-3.5 w-3.5 mr-1.5" /> Mobile preview</Button>
            <Button variant="outline" onClick={restoreDefaults} className="h-10 text-xs font-bold border-slate-300 text-slate-600" data-testid="menu-restore-defaults-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore defaults</Button>
            <Button onClick={save} disabled={saving || !dirty} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="menu-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {dirty ? 'Save menu' : 'Saved'}
            </Button>
          </div>

          {preview === 'desktop' && <DesktopPreview groups={cfg.groups} />}
          {preview === 'mobile' && <MobilePreview groups={cfg.groups} />}

          <div className="space-y-4">
            {cfg.groups.map((g, gi) => (
              <GroupCard
                key={g.id} group={g} gi={gi} groups={cfg.groups}
                onChange={(updated) => setGroups(cfg.groups.map((x, i) => (i === gi ? updated : x)))}
                onMove={(dir) => setGroups(move(cfg.groups, gi, gi + dir))}
                onDelete={() => {
                  if (window.confirm(`Delete the whole "${g.label}" group and its ${g.items.length} item(s)?\n\nTip: turn its visibility OFF instead if you might want it back.`)) {
                    setGroups(cfg.groups.filter((_, i) => i !== gi));
                  }
                }}
                onItemMoveToGroup={moveItemToGroup}
              />
            ))}
            <Button variant="outline" onClick={() => setGroups([...cfg.groups, EMPTY_GROUP()])} className="h-11 w-full text-sm font-bold border-dashed border-slate-300 text-slate-600" data-testid="menu-add-group-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add menu group
            </Button>
          </div>

          <Card className="border-slate-200 bg-slate-50"><CardContent className="p-4 text-xs text-slate-600 space-y-1">
            <p className="font-bold text-[#1a2744] flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> How the menus work</p>
            <p>• <strong>Desktop:</strong> visible groups become dropdown buttons in the top bar. Use the shorter desktop label to keep the bar clean.</p>
            <p>• <strong>Mobile:</strong> visible groups become accordion sections in the right hamburger menu with large tap targets.</p>
            <p>• <strong>Section links</strong> like /elections/key-dates or /support-pita#share jump straight to that part of the page, allowing for the sticky header.</p>
            <p>• <strong>External links</strong> get an “opens in new tab” marker automatically when Open in new tab is on.</p>
            <p>• Remember to press <strong>Save menu</strong> — changes only go live after saving.</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
